import { useCallback, useState } from "react";

/**
 * Camada de rede compartilhada pelo super admin.
 *
 * O backend RECUSA configurações impossíveis e devolve a EXPLICAÇÃO em `message`
 * ("Desvio para trás cria loop infinito...", "A idade mínima não pode ser maior..."):
 * é essa mensagem que ensina o admin. Aqui a gente a preserva e mostra como está —
 * um "erro 400" genérico jogaria fora a parte útil.
 */

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

/**
 * A API tem DUAS formas de erro, e a diferença importa:
 *   - erro de negócio: { message: "Banner not found" }                        -> string
 *   - erro de validação: { message: [{ message: "body.url is Required" }] }   -> ARRAY
 *
 * Tratar os dois como string faz a tela mostrar "[object Object]" — que não diz nada
 * ao admin e ainda parece um bug do painel. Aqui os dois viram texto legível.
 */
function textoDoErro(m) {
  if (typeof m === "string") return m;
  if (Array.isArray(m))
    return m
      .map((x) => (typeof x === "string" ? x : x?.message || JSON.stringify(x)))
      .filter(Boolean)
      .join(" · ");
  if (m && typeof m === "object") return m.message || JSON.stringify(m);
  return null;
}

async function lerErro(r) {
  if (r.status === 401 || r.status === 403)
    return "Acesso negado. Você precisa estar logado como administrador.";
  let d = null;
  try {
    d = await r.json();
  } catch {
    /* corpo vazio */
  }
  const t = d && (textoDoErro(d.message) || textoDoErro(d.error));
  return t || `Erro na requisição (${r.status}).`;
}

export async function apiGet(url) {
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) throw new Error(await lerErro(r));
  return r.json();
}

export async function apiSend(url, method, body) {
  const r = await fetch(url, {
    method,
    headers: authHeaders(),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!r.ok) throw new Error(await lerErro(r));
  return r.status === 204 ? null : r.json();
}

/**
 * Upload de imagem -> Bunny (CDN). Devolve a URL pública.
 *
 * Sem `Content-Type` de propósito: com FormData, o navegador precisa montar o
 * `multipart/form-data; boundary=...` sozinho. Se a gente fixar o header, o boundary
 * some e o multer do backend não acha o arquivo.
 */
export async function apiUploadImagem(file) {
  const fd = new FormData();
  fd.append("file", file);

  const r = await fetch(API_UPLOAD_IMAGEM, {
    method: "POST",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    body: fd,
  });
  if (!r.ok) throw new Error(await lerErro(r));
  const { url } = await r.json();
  return url;
}

const API_UPLOAD_IMAGEM = `${
  process.env.REACT_APP_API_URL || "https://api.reinoplay.app/api/v1"
}/uploads/thumbnail`;

/** Estado comum de tela: carregando / erro / toast. */
export function useAdminState() {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [toast, setToast] = useState(null);

  const rodar = useCallback(async (fn, msgSucesso) => {
    setErro(null);
    setCarregando(true);
    try {
      const r = await fn();
      if (msgSucesso) {
        setToast(msgSucesso);
        setTimeout(() => setToast(null), 3200);
      }
      return r;
    } catch (e) {
      setErro(e.message);
      return null;
    } finally {
      setCarregando(false);
    }
  }, []);

  return {
    carregando,
    erro,
    toast,
    rodar,
    limparErro: () => setErro(null),
  };
}

/** Blocos visuais que toda tela usa (erro em destaque + toast de sucesso). */
export function Erro({ erro, onFechar }) {
  if (!erro) return null;
  return (
    <div className="adm__erro" role="alert">
      <span>{erro}</span>
      <button onClick={onFechar} aria-label="Fechar">
        ✕
      </button>
    </div>
  );
}

export function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className="adm__toast">
      <span className="adm__toast--ok">✓</span>
      <span>{toast}</span>
    </div>
  );
}
